import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from '../config/api';
import {
  formatDailyHoursLabel,
  formatPromotionGuestBadge,
} from '../utils/promotionSchedule';

interface Promotion {
  id: string;
  cafe_id: string;
  cafe_name?: string;
  title: string;
  description: string;
  discount_percentage?: number;
  discount_amount?: number;
  promotion_type?: string | null;
  deal_type?: string | null;
  bogo_off_percent?: number | null;
  campaign_placement?: string | null;
  valid_from: string;
  valid_until: string;
  daily_start_time?: string | null;
  daily_end_time?: string | null;
  daily_hours_label?: string;
  image_url?: string;
}

export default function Promotions() {
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPromotions();
  }, []);

  const fetchPromotions = async () => {
    try {
      const response = await axios.get('/api/promotions');
      setPromotions(response.data || []);
    } catch (error) {
      console.error('Error fetching promotions:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDiscount = (promo: Promotion) => formatPromotionGuestBadge(promo);

  if (loading) {
    return <div className="container mx-auto px-4 py-8 text-center">Loading promotions...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6 text-amber-800">Promotions & Offers</h1>

      {promotions.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <p className="text-gray-600 mb-4">No active promotions at the moment.</p>
          <p className="text-sm text-gray-500">Check back soon for special offers!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {promotions.map(promo => (
            <Link
              key={promo.id}
              to={`/cafe/${promo.cafe_id}`}
              className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition"
            >
              {promo.image_url && (
                <div className="h-48 bg-amber-200 overflow-hidden">
                  <img
                    src={promo.image_url}
                    alt={promo.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-lg font-bold text-amber-800 flex-1">{promo.title}</h3>
                  <span className="bg-red-500 text-white px-2 py-1 rounded text-xs font-bold ml-2">
                    {formatDiscount(promo)}
                  </span>
                </div>
                {promo.cafe_name && (
                  <p className="text-sm text-gray-600 mb-2">📍 {promo.cafe_name}</p>
                )}
                <p className="text-gray-700 text-sm mb-3">{promo.description}</p>
                <p className="text-xs text-gray-500">
                  {promo.daily_hours_label || formatDailyHoursLabel(promo)}
                  {' · '}
                  Valid until: {new Date(promo.valid_until).toLocaleDateString()}
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
